import React, { FC, useEffect, useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImageProps } from "gatsby-plugin-image";
import { LANGUAGE_KEY } from '../../plugins/i18next/types';
import { useLocation } from "@reach/router";

interface SiteMetadata {
  description: string;
  language: string;
  siteUrl: string;
  title: string;
}

interface SiteData {
  siteMetadata: {
    locale: SiteMetadata[];
  };
}

interface SeoImageNode {
  name: string;
  ext: string;
  publicURL: string;
  childImageSharp?: {
    gatsbyImageData: GatsbyImageProps;
  };
}

interface LocaleNode {
  ns: string;
  language: string;
  data: string;
}

interface SEOQueryData {
  site: SiteData;
  seoimages: {
    edges: {
      node: SeoImageNode;
    }[];
  };
  locales: {
    edges: {
      node: LocaleNode;
    }[];
  };
  allMarkdownRemark: {
    edges: {
      node: {
        frontmatter: {
          blogimage: {
            publicURL: string;
          };
          title: string;
        };
      };
    }[];
  };
}

interface SEOProps {
  title: string;
  description?: string;
}

const SEO: FC<SEOProps> = ({ title, description = "" }) => {
  const [language, setLanguage] = useState<string>("en");
  const location = useLocation();

  const supportedLanguages = (process.env.GATSBY_LANGUAGES_LANGUAGES as string).split(',');
  const getLanguageFromPath = () => {
    const firstSegment = location.pathname.split('/')[1];
    if (supportedLanguages.includes(firstSegment)) {
      return firstSegment;
    }
    return 'en'; // Default language if none is found
  };

  const { site, seoimages, locales, allMarkdownRemark }: SEOQueryData = useStaticQuery(
    graphql`
      query($language: String) {
        site(siteMetadata: {locale: {elemMatch: {language: {eq: $language}}}}) {
          siteMetadata {
            locale {
              description
              language
              siteUrl
              title
            }
          }
        }
        seoimages: allFile(
          filter: {sourceInstanceName: {eq: "seoimages"}, name: {eq: "social"}}
        ) {
          edges {
            node {
              name
              ext
              publicURL
              childImageSharp {
                gatsbyImageData
              }
            }
          }
        }
        locales: allLocale(filter: {language: {eq: $language}, ns: {in: "pages"}}) {
          edges {
            node {
              ns
              language
              data
            }
          }
        }
        allMarkdownRemark {
          edges {
            node {
              frontmatter {
                title
                blogimage {
                  publicURL
                }
              }
            }
          }
        }
      }
    `,
  );
  
  const filterByLanguage = () => {
 const lang = getLanguageFromPath();
     const filteredNode = locales.edges.find(
      (edge) => edge.node.language === lang
    );
    return filteredNode ? JSON.parse(filteredNode.node.data) : {};
  };

  const getPageImage = () => {
    const blogPost = allMarkdownRemark.edges.find(
      (edge) => edge.node.frontmatter.title === title
    );
    return blogPost !== undefined  ? blogPost.node.frontmatter?.blogimage?.publicURL : `${sitedetails?.siteUrl}${seoimages.edges[0].node.publicURL}`;
  }

  const getTranslationValue = (key: string) => {
    const translations = filterByLanguage();
    if (!key) return "";
    const keys = key.split('.');
    let value = translations;
    for (const k of keys) {
      value = value ? value[k] : null;
    }
    return value || key;
  };
  
  useEffect(() => {
    try {
      const storedLanguage = localStorage.getItem(LANGUAGE_KEY);
      setLanguage(storedLanguage ?? 'en');
    } catch (error) {
      console.error('Error accessing localStorage', error);
      setLanguage('en');
    }
  }, []);



  title = getTranslationValue(title);
  description = getTranslationValue(description);

  const currentLanguage = language;
  const sitedetails = site.siteMetadata.locale.find(
    (s) => s.language === currentLanguage
  );

  if (!sitedetails) return null;

  const canonicalUrl = `${sitedetails.siteUrl}${location.pathname}`;  
  const pageImage = getPageImage();
  const siteTitle = `EasyLife 365 - ${title ?? sitedetails.title}`;
  const siteDescriptin = description ?? sitedetails.description;

  return (
    <>
      <title>{siteTitle}</title>
      <meta charSet="utf-8" />
      <meta httpEquiv="x-ua-compatible" content="ie=edge" />
      <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
      <meta name="description" content={siteDescriptin} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:description" content={siteDescriptin} />
      <meta name="twitter:card" content='summary_large_image' />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:description" content={siteDescriptin} />
      <meta name="twitter:image" content={pageImage} />
    </>
  );
};

export default SEO;
